import * as React from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import {
  ALTERNATIVE_EMAIL_CACHE_DAYS,
  ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS,
  ALTERNATIVE_EMAIL_NEVER_EXPIRY,
  STORAGE_KEY,
} from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import useCheckSignificantCourseProgress from 'bundles/enterprise-legacy-learner-home/queryHooks/useSignificantCourseProgress';
import type { RecoveryEmail } from 'bundles/enterprise-legacy-learner-home/types/recoveryEmail';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';

const useAlternativeEmailModal = (
  userId: number,
  userEmails: UserEmail[],
  programId: string,
  recoveryEmail: RecoveryEmail | undefined | null
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = React.useState(false);
  const hasSignificantCourseProgress = useCheckSignificantCourseProgress(programId);

  const setModalQueryParam = (isOpen: boolean) => {
    navigate({
      pathname: location.pathname,
      search: '?' + new URLSearchParams({ ...location.query, ...{ openRecoveryModal: `${isOpen}` } }).toString(),
    });
  };

  React.useEffect(() => {
    const hasVerifiedSecondaryEmail = userEmails.some((email) => !email.isPrimary && email.isVerified);

    if (recoveryEmail && recoveryEmail.isVerified) {
      setModalOpen(false);
      storageUtils.set(userId, programId, STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    if (hasVerifiedSecondaryEmail) {
      setModalOpen(false);
      storageUtils.set(userId, programId, STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    const shouldOpenAfterStoragePersistedDuration =
      storageUtils.get(userId, programId, STORAGE_KEY, Infinity) < Date.now();

    if (shouldOpenAfterStoragePersistedDuration && hasSignificantCourseProgress) {
      setModalQueryParam(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmails, programId, hasSignificantCourseProgress]);

  React.useEffect(() => {
    setModalOpen(location.query.openRecoveryModal === 'true');
  }, [location.query.openRecoveryModal]);

  const onClose = () => {
    setModalQueryParam(false);
  };

  const onDefer = () => {
    const dismissedForDuration = hasSignificantCourseProgress
      ? ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS
      : ALTERNATIVE_EMAIL_CACHE_DAYS;
    storageUtils.set(userId, programId, STORAGE_KEY, Date.now() + dismissedForDuration);
    onClose();
  };

  return {
    isOpen: isModalOpen,
    onClose: hasSignificantCourseProgress ? onDefer : onClose,
    shouldDeferOnClose: hasSignificantCourseProgress,
  };
};

export default useAlternativeEmailModal;
